import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { DialogService } from '../../../../../core/services/dialog.service';
import { InputsModule } from '../../../../common/inputs/inputs.module';
import { MatButtonModule } from '@angular/material/button';
import { Certificate } from '../../../../../core/models/certificate';
import { CertificatesService } from '../../../../../core/services/api/certificates.service';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { handlePluralTranslation } from '../../../../../core/utils';
import { forkJoin } from 'rxjs';
import { SnackBarService } from '../../../../../core/services/snack-bar.service';

@Component({
  selector: 'ling-certificate-clone-dialog',
  standalone: true,
  imports: [
    CommonModule,
    InputsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogModule,
  ],
  templateUrl: './certificate-clone-dialog.component.html',
  styleUrls: ['./certificate-clone-dialog.component.scss'],
})
export class CertificateCloneDialogComponent {
  protected dialogData = inject<{ certificates: Certificate[] }>(
    MAT_DIALOG_DATA
  );
  protected dialog = inject(DialogService);
  protected message = inject(SnackBarService);
  protected translateService = inject(TranslateService);
  protected certificatesService = inject(CertificatesService);

  protected cloneForm = new FormGroup({
    amount: new FormControl(1, [Validators.min(1), Validators.max(1000)]),
  });

  get singleCertificate() {
    return this.dialogData.certificates.length === 1
      ? this.dialogData.certificates[0]
      : undefined;
  }

  get multipleCertificates() {
    return this.dialogData.certificates.length > 1
      ? this.dialogData.certificates
      : undefined;
  }

  get title(): string {
    return this.getCloneTranslation('titleSingle', 'titleMultiple', {
      multiple: {
        amount: this.multipleCertificates?.length,
        certificateForm: this.translateService.instant(
          handlePluralTranslation(
            this.multipleCertificates?.length,
            'certificate'
          )
        ),
      },
    });
  }

  get text(): string {
    return this.getCloneTranslation('textSingle', 'textMultiple', {
      single: { certificateName: this.singleCertificate?.name },
      multiple: {
        amount: this.multipleCertificates?.length,
      },
    });
  }

  get confirmButtonText(): string {
    return this.getCloneTranslation('confirmSingle', 'confirmMultiple');
  }

  getCloneTranslation(
    singleKey: string,
    multipleKey: string,
    config?: { single?: any; multiple?: any; prefix?: string }
  ) {
    if (this.singleCertificate) {
      return this.translateService.instant(
        `${config?.prefix ?? 'dialogs.cloneCertificate'}.${singleKey}`,
        config?.single
      );
    }
    if (this.multipleCertificates) {
      return this.translateService.instant(
        `${config?.prefix ?? 'dialogs.cloneCertificate'}.${multipleKey}`,
        config?.multiple
      );
    }

    return '';
  }

  onContinue() {
    if (!this.cloneForm.valid) {
      return;
    }
    const calls = new Array(this.cloneForm.value.amount!)
      .fill(0)
      .map(() =>
        this.certificatesService.cloneCertificates(this.dialogData.certificates)
      );
    forkJoin(calls).subscribe(() => {
      this.message.success(
        this.getCloneTranslation(
          'cloneCertificateSingle',
          'cloneCertificateMultiple',
          {
            prefix: 'snackbar.success',
            single: {
              certificateName: this.singleCertificate?.name,
            },
            multiple: {
              amount: this.multipleCertificates?.length,
            },
          }
        )
      );
      this.dialog.closeCurrent(this.cloneForm.value.amount);
    });
  }
}
